/**
 * Modal "Agregar / Editar vehículo" — extraído de MisDatos.tsx en Sprint
 * 18 P2-B2 para reusarlo desde el wizard de onboarding sin duplicar.
 *
 * - Alta: POST vehículo con patente (el backend canoniza, P6) + tracción.
 * - Edición: PATCH sin patente (inmutable, 422 si viene); si el vehículo
 *   está aprobado exige justificación del cambio (S15-Vec.3).
 */
import { useState, type FormEvent } from 'react';

import { ApiError } from '../../lib/api/client';
import { actualizarVehiculo, crearVehiculo } from '../../lib/api/vehiculos';
import type { ColorVehiculo, VehiculoRead } from '../../lib/api/types';
import { COLORES_VEHICULO } from '../../lib/api/types';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

interface ModalVehiculoProps {
  loteId: string;
  inicial: VehiculoRead | null;
  onCerrar: () => void;
  onGuardado: () => void;
  /** Suprime la oferta de pedir un control nuevo. El wizard de onboarding
   *  lo activa cuando hay un control de sobra en la bolsa (se asigna desde
   *  Mis Datos) O cuando la casa está en el tope de CR configurado (no se
   *  ofrece algo que no se puede dar). Mis Datos lo activa si hay alguno en
   *  la bolsa. Default false (se puede pedir). El nombre es histórico (F5);
   *  hoy significa "no ofrecer pedir", no solo "hay en bolsa". */
  hayControlesEnBolsa?: boolean;
}

export function ModalVehiculo({
  loteId,
  inicial,
  onCerrar,
  onGuardado,
  hayControlesEnBolsa = false,
}: ModalVehiculoProps) {
  const esEdicion = inicial !== null;
  // S15-Vec.3: si edita un vehículo aprobado, exige justificación.
  const exigeJustificacion = esEdicion && inicial.estado === 'aprobado';
  // Estado rechazado: el vecino corrige en respuesta al motivo; puede
  // adjuntar una aclaración OPCIONAL. Al guardar vuelve a
  // pendiente_validacion (el backend cierra el ciclo de rechazo).
  const esRechazado = esEdicion && inicial.estado === 'rechazado';
  const [patente, setPatente] = useState(inicial?.patente ?? '');
  const [marca, setMarca] = useState(inicial?.marca ?? '');
  const [modelo, setModelo] = useState(inicial?.modelo ?? '');
  const [color, setColor] = useState<ColorVehiculo | ''>(inicial?.color ?? '');
  const [colorOtro, setColorOtro] = useState(inicial?.color_otro ?? '');
  const [notas, setNotas] = useState(inicial?.notas ?? '');
  const [justificacion, setJustificacion] = useState('');
  // Sprint 17 S17-18 — checkbox "¿necesita CR?" solo al crear.
  const [necesitaCr, setNecesitaCr] = useState(false);
  // Sprint 18 P6: 2 checkboxes mutex que mapean a `tipo_traccion`.
  // Ambos desmarcados → 'combustion'. Solo uno marcado a la vez.
  const [esHibrido, setEsHibrido] = useState(
    inicial?.tipo_traccion === 'hibrido',
  );
  const [esElectrico, setEsElectrico] = useState(
    inicial?.tipo_traccion === 'electrico',
  );
  const tipoTraccion: 'combustion' | 'hibrido' | 'electrico' = esElectrico
    ? 'electrico'
    : esHibrido
      ? 'hibrido'
      : 'combustion';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Si color != 'Otro', no se guarda texto libre (lo enforce el backend).
  const colorOtroVisible = color === 'Otro';
  // S14-09 review: marca, modelo, color son obligatorios. Notas es opcional.
  // S15-Vec.3: si exigeJustificacion, también el textarea debe tener texto.
  const valido =
    patente.trim().length > 0 &&
    marca.trim().length > 0 &&
    modelo.trim().length > 0 &&
    color !== '' &&
    (!colorOtroVisible || colorOtro.trim().length > 0) &&
    (!exigeJustificacion || justificacion.trim().length > 0);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Sprint 18 P6: en edición la patente NO se envía (backend la rechaza
      // con 422 si viene con valor). Solo el alta normaliza y manda patente.
      const camposComunes = {
        marca: marca.trim() || null,
        modelo: modelo.trim() || null,
        color: color || null,
        // S14-08: color_otro solo se envia cuando color='Otro'; en
        // cualquier otro caso el backend exige NULL (CHECK constraint).
        color_otro: color === 'Otro' ? colorOtro.trim() || null : null,
        notas: notas.trim() || null,
        tipo_traccion: tipoTraccion,
      };
      if (esEdicion) {
        await actualizarVehiculo(inicial.id, {
          ...camposComunes,
          // S15-Vec.3: el backend exige justificación cuando se edita
          // un vehículo aprobado y hay cambios efectivos. En rechazado es
          // opcional: solo se envía si el vecino la escribió. En otros
          // estados, undefined es seguro.
          justificacion_cambio:
            exigeJustificacion || (esRechazado && justificacion.trim())
              ? justificacion.trim()
              : undefined,
        });
      } else {
        // Sprint 18 P6: el backend es la única fuente de verdad para
        // la normalización (Pydantic mode='before' canoniza). Mandamos
        // input crudo (trim+upper opcional) y respetamos su respuesta.
        await crearVehiculo(loteId, {
          ...camposComunes,
          patente: patente.trim().toUpperCase(),
          // F5: si hay bolsa, nunca se pide (el checkbox no se muestra).
          necesita_cr: hayControlesEnBolsa ? false : necesitaCr,
        });
      }
      onGuardado();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={esEdicion ? 'Editar vehículo' : 'Agregar vehículo'}
      onClose={onCerrar}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="form-vehiculo"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : esEdicion ? 'Guardar' : 'Agregar'}
          </Button>
        </>
      }
    >
      <form id="form-vehiculo" onSubmit={handleSubmit} className="space-y-4">
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Patente <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            value={patente}
            onChange={(e) => setPatente(e.target.value)}
            placeholder="BCDF-12 o AB-1234"
            maxLength={10}
            disabled={esEdicion}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
            required
          />
          <span className="text-xs text-text-muted block mt-1">
            {esEdicion
              ? 'La patente no se puede modificar. Para cambio de vehículo, da de baja éste y crea uno nuevo.'
              : 'Escribe la patente con o sin guion; al guardar se formatea sola.'}
          </span>
        </label>

        {/* Sprint 18 P6: tipo de tracción — 2 checkboxes mutex.
            Ambos vacíos → combustion (bencina/diesel). */}
        <fieldset className="space-y-2 border border-white/10 rounded-sm p-3">
          <legend className="text-sm text-text-secondary px-1">
            ¿Vehículo con batería de litio?
          </legend>
          <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={esHibrido}
              onChange={(e) => {
                setEsHibrido(e.target.checked);
                if (e.target.checked) setEsElectrico(false);
              }}
              className="rounded-sm accent-orange"
            />
            <span>Híbrido</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={esElectrico}
              onChange={(e) => {
                setEsElectrico(e.target.checked);
                if (e.target.checked) setEsHibrido(false);
              }}
              className="rounded-sm accent-orange"
            />
            <span>Eléctrico</span>
          </label>
          <p className="text-xs text-text-muted">
            Si no marcas ninguno, el vehículo es a bencina o diésel. Este dato
            es importante para el catastro de Bomberos: ante una emergencia
            necesitan saber qué vehículos tienen batería (híbrido/eléctrico).
          </p>
        </fieldset>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Marca <span className="text-state-danger">*</span>
            </span>
            <input
              type="text"
              value={marca}
              onChange={(e) => setMarca(e.target.value)}
              maxLength={50}
              required
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Modelo <span className="text-state-danger">*</span>
            </span>
            <input
              type="text"
              value={modelo}
              onChange={(e) => setModelo(e.target.value)}
              maxLength={50}
              required
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Color <span className="text-state-danger">*</span>
            </span>
            <select
              value={color}
              onChange={(e) => {
                const nuevo = e.target.value as ColorVehiculo | '';
                setColor(nuevo);
                // Si el usuario sale de "Otro", limpia el texto libre.
                if (nuevo !== 'Otro') setColorOtro('');
              }}
              required
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            >
              <option value="" disabled>
                Elige un color
              </option>
              {COLORES_VEHICULO.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          {colorOtroVisible && (
            <label className="text-sm space-y-1">
              <span className="text-text-secondary">
                Especificar color <span className="text-state-danger">*</span>
              </span>
              <input
                type="text"
                value={colorOtro}
                onChange={(e) => setColorOtro(e.target.value)}
                placeholder="Ej. Verde militar"
                maxLength={40}
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
                required
              />
            </label>
          )}
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Notas <span className="text-text-muted">(opcional)</span>
          </span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>

        {(exigeJustificacion || esRechazado) && (
          <label className="text-sm space-y-1 block">
            <span className="text-text-secondary">
              {exigeJustificacion ? (
                <>
                  Justificación del cambio{' '}
                  <span className="text-state-danger">*</span>
                </>
              ) : (
                <>
                  Aclaración para la Comisión{' '}
                  <span className="text-text-muted">(opcional)</span>
                </>
              )}
            </span>
            <textarea
              value={justificacion}
              onChange={(e) => setJustificacion(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder={
                exigeJustificacion
                  ? 'Ej: Corrección de tipeo en marca, cambio de patente por reemplazo.'
                  : 'Si la Comisión pidió una aclaración en el motivo, escríbela aquí.'
              }
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              required={exigeJustificacion}
            />
            <span className="text-xs text-text-muted block">
              Al guardar, el vehículo volverá a estar pendiente de revisión por
              la Comisión.
            </span>
          </label>
        )}

        {/* Sprint 17 S17-18 — checkbox solo al CREAR vehículo, no editar.
            Se omite si no corresponde ofrecer pedir un control (hay uno de
            sobra en la bolsa, o la casa está en el tope de CR). */}
        {!esEdicion &&
          (hayControlesEnBolsa ? (
            <p className="text-xs text-text-muted">
              No es necesario solicitar un control aquí. Los controles remotos
              de tu casa se gestionan desde Mis Datos → Recursos de tu casa.
            </p>
          ) : (
            <label className="flex items-start gap-2 text-sm text-text-primary cursor-pointer">
              <input
                type="checkbox"
                checked={necesitaCr}
                onChange={(e) => setNecesitaCr(e.target.checked)}
                className="mt-0.5 rounded-sm accent-orange"
              />
              <span>
                <span>¿Este vehículo necesita un control remoto?</span>
                <span className="block text-xs text-text-muted mt-0.5">
                  Si marcas, se enviará automáticamente una solicitud de control
                  remoto a la Comisión junto con el alta del vehículo.
                </span>
              </span>
            </label>
          ))}

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
